import { TableColumn, TableStyles } from 'react-data-table-component';
import { WfBranchSummaryResponse } from 'src/api/wf/models/WfSummary';
import LinkButton from 'src/ui/wf/common/button/LinkButton';

const fmt = (val?: number | null) => val?.toLocaleString() ?? '0';
const formatRate = (val?: string | number | null) => {
  if (val === undefined || val === null) return '0.00';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '0.00';
  return (num * 100).toFixed(2);
};

export const mapSingleBranchToSituation = (
  item: WfBranchSummaryResponse,
  nameOverride?: string
): WfBranchSummaryResponse => {
  return {
    ...item,
    branchName: nameOverride || item.branchName,
    branchCode: item.branchCode || '',
    salesOfficeCode: item.salesOfficeCode || '',
    salesOfficeName: item.salesOfficeName || '',
  };
};

const NUMERIC_COLUMN_WIDTH = '7rem';

export const branchSummaryCustomStyles: TableStyles = {
  tableWrapper: {
    style: {
      display: 'block',
      overflow: 'visible',
    },
  },
  table: {
    style: {
      backgroundColor: 'transparent',
      width: '100%',
      tableLayout: 'fixed',
    },
  },
  rows: {
    style: {
      minHeight: '2.25rem',
      borderBottom: '1px solid #d1d5db',
    },
  },
  cells: {
    style: {
      borderRight: '1px solid #d1d5db',
      padding: '0px',
      fontSize: '0.75rem',
    },
  },
};

const rightCol = (
  name: string,
  render: (row: WfBranchSummaryResponse) => string | number,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<WfBranchSummaryResponse> => ({
  name,
  selector: (row) => render(row).toString(),
  cell: (row) => {
    const isTotal = row.salesOfficeCode === '';
    return (
      <div className={`flex w-full items-center justify-end pr-2 ${isTotal ? 'my-4 ' : ''}`}>
        {render(row)}
      </div>
    );
  },
  width,
});

const percentCol = (
  name: string,
  render: (row: WfBranchSummaryResponse) => string,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<WfBranchSummaryResponse> => ({
  name,
  selector: render,
  cell: (row) => <div className="w-full pr-2 text-right">{render(row)}%</div>,
  width,
});

export const branchSummaryColumns = (
  onBusinessLocationClick: (branchCode: string, salesOfficeCode: string) => void
): TableColumn<WfBranchSummaryResponse>[] => {
  return [
    {
      name: 'Branch',
      selector: (row) => row.branchCode,
      cell: (row) => <div className="w-full text-center font-bold">{row.branchName}</div>,
      width: NUMERIC_COLUMN_WIDTH,
    },
    {
      name: 'Location',
      selector: (row) => row.salesOfficeName,
      cell: (row) => (
        <LinkButton
          title={row.salesOfficeName}
          onClick={() => onBusinessLocationClick(row.branchCode, row.salesOfficeCode)}
          className="w-full justify-center"
        />
      ),
      width: NUMERIC_COLUMN_WIDTH,
    },
    rightCol('Products', (row) => fmt(row.products)),
    rightCol('Incidents', (row) => fmt(row.confinement?.incidentItems)),
    rightCol('Completed', (row) => fmt(row.confinement?.completedItems)),
    percentCol('Comp. Rate', (row) => formatRate(row.confinement?.completedRate)),
    rightCol('Alert', (row) => fmt(row.currentSituation?.alert)),
    rightCol('On Call', (row) => fmt(row.currentSituation?.onCall)),
    rightCol('Outage', (row) => fmt(row.currentSituation?.outage)),
    rightCol('Others', (row) => fmt(row.currentSituation?.otherAlerts)),
    rightCol('Parts', (row) => fmt(row.currentSituation?.parts)),
    rightCol('Flood', (row) => fmt(row.currentSituation?.flood)),
    rightCol('Inaccess.', (row) => fmt(row.currentSituation?.inaccessible)),
    rightCol('Recovered', (row) => fmt(row.currentSituation?.recovered)),
  ];
};
